import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [adding, setAdding] = useState(false);

  const savings = product.compare_price ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;
  const wishlisted = isWishlisted(product.id);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Please login to add to cart'); return; }
    setAdding(true);
    const result = await addToCart(product.id, 1);
    if (result?.success) {
      toast.success(`${product.name} added to cart`);
    } else if (result?.error) {
      toast.error(result.error === 'login_required' ? 'Please login first' : result.error);
    }
    setAdding(false);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Please login to save to wishlist'); return; }
    const result = await toggleWishlist(product.id);
    if (result?.error === 'login_required') { toast.error('Please login first'); return; }
    if (result?.error) { toast.error('Failed to update wishlist'); return; }
    toast.success(result.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
  };

  return (
    <Link to={`/products/${product.id}`} className="product-card block group" data-testid={`product-card-${product.id}`}>
      {/* Image */}
      <div className="relative overflow-hidden bg-surface-2" style={{ aspectRatio: '1/1' }}>
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1773325076564-502be7dd4eba?w=400&q=60'}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {savings > 0 && (
            <span className="tag-orange" data-testid="savings-badge">-{savings}%</span>
          )}
          {product.featured && (
            <span className="tag-yellow flex items-center gap-0.5"><Zap size={9} />HOT</span>
          )}
          {product.stock <= 5 && product.stock > 0 && (
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 uppercase">Low Stock</span>
          )}
          {product.stock === 0 && (
            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 uppercase">Out of Stock</span>
          )}
        </div>
        {/* Wishlist button */}
        <button
          onClick={handleWishlist}
          className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-black/60 hover:bg-primary/20 border border-white/10 hover:border-primary transition-all ${wishlisted ? 'text-primary' : 'text-white/60'}`}
          data-testid={`wishlist-btn-${product.id}`}
        >
          <Heart size={14} fill={wishlisted ? 'currentColor' : 'none'} />
        </button>
        {/* Quick add overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/80 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <button
            onClick={handleAddToCart}
            disabled={adding || product.stock === 0}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary hover:bg-white text-black font-bold text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
            data-testid={`quick-add-btn-${product.id}`}
          >
            <ShoppingCart size={13} />
            {adding ? 'Adding...' : product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-0.5">{product.brand}</p>
        <h3 className="text-sm font-body font-semibold text-white leading-snug mb-2 line-clamp-2 group-hover:text-primary/90 transition-colors" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>

        {/* Specs snippet */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
            {Object.entries(product.specs).slice(0, 2).map(([k, v]) => `${v}`).join(' · ')}
          </p>
        )}

        {/* Rating */}
        {product.reviews_count > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={10} className={s <= Math.round(product.avg_rating) ? 'text-primary fill-primary' : 'text-white/20'} />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">({product.reviews_count})</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-end justify-between mt-auto">
          <div>
            <div className="price-tag text-lg font-black text-primary leading-none" data-testid={`product-price-${product.id}`}>
              AED {product.price.toLocaleString()}
            </div>
            {product.compare_price && (
              <div className="text-xs text-muted-foreground line-through">AED {product.compare_price.toLocaleString()}</div>
            )}
          </div>
          {product.uae_market_price && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Market</div>
              <div className="text-xs font-mono text-green-400">AED {product.uae_market_price}</div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
